import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMapsLoader } from "../../hooks/googleMapsLoader";

/**
 * ServiceAreaMap — renders all service area polygons on a single Google Map.
 *
 * Introduced in Session 30 to give users instant visual coverage awareness on
 * the /service-areas page. Reusable in `edit` mode for the create/edit modal
 * context render, though the modals currently layer context via
 * DrawGoogleMap directly (which shares the same map instance as the drawing).
 *
 * Geometry is expected as parsed GeoJSON (`{ type: 'Polygon', coordinates:
 * [[[lng, lat], ...]] }`) — this is what the backend @AfterLoad hook produces
 * when `includeGeometry=true` is passed.
 */

// Rotating color palette — 8 distinct hues picked to stay legible on the
// default Google Maps roadmap style. Order chosen so adjacent list positions
// don't collide.
const PALETTE = [
  "#4285F4", // blue
  "#DB4437", // red
  "#F4B400", // amber
  "#0F9D58", // green
  "#AB47BC", // purple
  "#00ACC1", // teal
  "#FF7043", // orange
  "#5C6BC0", // indigo
];

export function colorForIndex(idx: number): string {
  return PALETTE[idx % PALETTE.length];
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // geographic center of the US

export interface ServiceAreaMapItem {
  id: string;
  name?: string | null;
  geometry?: any; // GeoJSON polygon or null
  color?: string | null; // Optional user-selected hex; falls back to palette rotation.
}

interface ServiceAreaMapProps {
  serviceAreas: ServiceAreaMapItem[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  onEdit?: (item: ServiceAreaMapItem) => void;
  mode?: "readonly" | "edit";
  /** In edit mode, the id of the area currently being edited (excluded from context render). */
  editingId?: string | null;
  height?: number | string;
  /** Called once the map has finished mounting. Rarely needed. */
  onMapReady?: (map: google.maps.Map) => void;
}

export default function ServiceAreaMap(props: ServiceAreaMapProps) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  if (loadError) return <div style={{ padding: 12, color: "#A60000" }}>Error loading maps</div>;
  if (!isLoaded) return <div style={{ padding: 12, color: "#818181" }}>Loading map…</div>;
  return <TheMap {...props} />;
}

function TheMap({
  serviceAreas,
  selectedId,
  onSelect,
  onEdit,
  mode = "readonly",
  editingId,
  height = 480,
  onMapReady,
}: ServiceAreaMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonsRef = useRef<Map<string, google.maps.Polygon>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const boundsFittedRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeInfoId, setActiveInfoId] = useState<string | null>(null);

  // Build a stable index → color map keyed by service area id, so a given
  // area keeps its color even if the list is reordered client-side. Areas
  // with an explicit `color` override the palette assignment.
  const colorById = useMemo(() => {
    const m: Record<string, string> = {};
    serviceAreas.forEach((a, idx) => {
      m[a.id] = a.color || colorForIndex(idx);
    });
    return m;
  }, [serviceAreas]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      infoWindowRef.current = new google.maps.InfoWindow();
      setMapLoaded(true);
      if (typeof onMapReady === "function") onMapReady(map);
    },
    [onMapReady]
  );

  const openInfoWindow = useCallback(
    (area: ServiceAreaMapItem, position: google.maps.LatLng) => {
      const iw = infoWindowRef.current;
      const map = mapRef.current;
      if (!iw || !map) return;

      const container = document.createElement("div");
      container.style.fontFamily = "Poppins, sans-serif";
      container.style.fontSize = "13px";
      container.style.padding = "2px 4px 0";
      container.style.minWidth = "160px";

      const nameEl = document.createElement("div");
      nameEl.style.fontWeight = "600";
      nameEl.style.color = "#000";
      nameEl.style.marginBottom = "6px";
      nameEl.textContent = area.name || "Service area";
      container.appendChild(nameEl);

      if (typeof onEdit === "function") {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = "Edit";
        btn.style.cssText =
          "background:#000;color:#fff;border:none;border-radius:4px;padding:4px 10px;font-size:12px;font-family:Poppins,sans-serif;cursor:pointer;";
        btn.onclick = () => {
          iw.close();
          setActiveInfoId(null);
          onEdit(area);
        };
        container.appendChild(btn);
      }

      iw.setContent(container);
      iw.setPosition(position);
      iw.open({ map });
      setActiveInfoId(area.id);
    },
    [onEdit]
  );

  // Reset "fitted" flag when the input list length changes.
  useEffect(() => {
    boundsFittedRef.current = false;
  }, [serviceAreas.length]);

  // (Re)render polygons whenever the input list or the mode changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    // Clear existing overlays.
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current.clear();

    const bounds = new google.maps.LatLngBounds();
    let renderedAny = false;

    serviceAreas.forEach((area) => {
      if (mode === "edit" && editingId && area.id === editingId) return;
      const geom = area.geometry;
      if (!geom || geom.type !== "Polygon" || !Array.isArray(geom.coordinates) || !geom.coordinates[0]) return;

      const path = geom.coordinates[0]
        .filter((pt: any) => Array.isArray(pt) && pt.length >= 2)
        .map(([lng, lat]: [number, number]) => new google.maps.LatLng(lat, lng));
      if (path.length < 3) return;

      const color = colorById[area.id] || PALETTE[0];
      const isEditMode = mode === "edit";
      const isSelected = !isEditMode && selectedId === area.id;

      const polygon = new google.maps.Polygon({
        paths: path,
        strokeColor: color,
        strokeOpacity: isEditMode ? 0.55 : 0.9,
        strokeWeight: isSelected ? 3 : isEditMode ? 1 : 2,
        fillColor: color,
        fillOpacity: isEditMode ? 0.08 : isSelected ? 0.4 : 0.22,
        clickable: !isEditMode,
        zIndex: isSelected ? 2 : 1,
      });
      polygon.setMap(map);

      if (!isEditMode) {
        polygon.addListener("click", (e: any) => {
          if (typeof onSelect === "function") onSelect(area.id);
          openInfoWindow(area, e?.latLng ?? path[0]);
        });
      }

      polygonsRef.current.set(area.id, polygon);
      path.forEach((pt: google.maps.LatLng) => bounds.extend(pt));
      renderedAny = true;
    });

    // Fit the map to the union of all polygons on first render, but don't
    // fight the user's zoom on subsequent updates.
    if (renderedAny && !boundsFittedRef.current) {
      map.fitBounds(bounds, 40);
      boundsFittedRef.current = true;
    }

    // Close any stale info window whose target no longer exists.
    if (activeInfoId && !polygonsRef.current.has(activeInfoId)) {
      infoWindowRef.current?.close();
      setActiveInfoId(null);
    }
  }, [serviceAreas, mode, editingId, mapLoaded, colorById]);

  // Reflect selectedId changes: restyle, pan/zoom, and open info window.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || mode === "edit") return;

    polygonsRef.current.forEach((poly, id) => {
      const isSel = id === selectedId;
      poly.setOptions({
        strokeWeight: isSel ? 3 : 2,
        fillOpacity: isSel ? 0.4 : 0.22,
        zIndex: isSel ? 2 : 1,
      });
    });

    if (selectedId) {
      const poly = polygonsRef.current.get(selectedId);
      if (poly) {
        const b = new google.maps.LatLngBounds();
        poly.getPath().forEach((p) => b.extend(p));
        map.fitBounds(b, 60);
        const area = serviceAreas.find((a) => a.id === selectedId);
        if (area) openInfoWindow(area, b.getCenter());
      }
    } else {
      infoWindowRef.current?.close();
      setActiveInfoId(null);
    }
  }, [selectedId, mapLoaded, mode]);

  // "Fit all" action — exposed via a small overlay button in readonly mode.
  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    polygonsRef.current.forEach((poly) => {
      poly.getPath().forEach((p) => bounds.extend(p));
      any = true;
    });
    if (any) map.fitBounds(bounds, 40);
    if (typeof onSelect === "function") onSelect(null);
  }, [onSelect]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: typeof height === "number" ? `${height}px` : height }}
        zoom={4}
        center={DEFAULT_CENTER}
        onLoad={onLoad}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
      />
      {mode === "readonly" && (
        <button
          type="button"
          onClick={fitAll}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: 3,
            padding: "6px 12px",
            fontSize: 12,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,.2)",
          }}
        >
          Fit all
        </button>
      )}
    </div>
  );
}
